// 時間前 札のフォーミュラ価格のプレビュー RPC の request と、 取得状況・応答から計算結果の表示と
// 手数料自動考慮の判定への写像 (AC-BIN-005c / AC-BIN-004b)。 1 コマの札 (札作成・入札編集) と
// 一括作成のコマごとで共通。 計算は backend で、 ここでは計算しない。
import { create } from '@bufbuild/protobuf'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  type PreviewBiddingIntradayOrderFormulaPriceRequest,
  PreviewBiddingIntradayOrderFormulaPriceRequestSchema,
  type PreviewBiddingIntradayOrderFormulaPriceResponse,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import {
  type FeeAdjustmentViolation,
  type FeeAutoConsiderBlockedReason,
  type FeeAutoConsiderPreview,
  type FeeAutoConsiderTreatment,
  resolveFeeAutoConsiderPreview,
  resolveFeeFallbackOffPreview,
} from '~/pages/IntradayBidding/models/feeAutoConsider'
import {
  type FormulaPricePreview,
  type IntradayFormulaInput,
  resolveFormulaPricePreview,
  toBiddingIntradayOrderFormula,
} from '~/pages/IntradayBidding/models/intradayFormula'

/** プレビューを問い合わせる札 (受渡日・コマ・札のエリア・売買) とフォーミュラ。 */
export type FormulaPricePreviewTarget = {
  targetDate: ProtoDate
  timeCode: number
  /** 札のエリア (手数料自動考慮と入札制限の判定に使う)。 */
  areaCode: AreaCode
  direction: Side
  formula: IntradayFormulaInput
}

export const toFormulaPricePreviewRequest = (
  target: FormulaPricePreviewTarget,
  treatment: FeeAutoConsiderTreatment,
): PreviewBiddingIntradayOrderFormulaPriceRequest =>
  create(PreviewBiddingIntradayOrderFormulaPriceRequestSchema, {
    targetDate: target.targetDate,
    timeCode: target.timeCode,
    areaCode: target.areaCode,
    direction: target.direction,
    formula: toBiddingIntradayOrderFormula(target.formula),
    treatFeeAutoConsiderAsOff: treatment.status === 'fallbackOff',
  })

export type FormulaPricePreviewResult = {
  price: FormulaPricePreview
  fee: FeeAutoConsiderPreview
}

/**
 * 取得状況と応答から、 計算結果の表示と、 計算結果を希望価格とした手数料自動考慮の判定を決める。
 * `idle` は入力が揃わず問い合わせていない間。 OFF として扱う間は、 backend が OFF として計算した
 * 入札価格 (計算結果のまま) かだけを見る (AC-BIN-004b)。
 */
export const resolveIntradayFormulaPricePreview = ({
  status,
  data,
  direction,
  treatment,
}: {
  status: 'idle' | 'loading' | 'error' | 'success'
  data: PreviewBiddingIntradayOrderFormulaPriceResponse | undefined
  direction: Side
  treatment: FeeAutoConsiderTreatment
}): FormulaPricePreviewResult => {
  const { price, feeStatus } = resolveFormulaPricePreview(status, data)
  return {
    price,
    fee:
      treatment.status === 'fallbackOff'
        ? resolveFeeFallbackOffPreview({
            status: feeStatus,
            result: data?.orderPrice,
            setting: treatment.setting,
          })
        : resolveFeeAutoConsiderPreview({
            status: feeStatus,
            state: data?.feeAutoConsider,
            result: data?.orderPrice,
            side: direction,
            treatment,
          }),
  }
}

/** 1 コマのフォーミュラの札を作成・保存してよいか。 */
export type FormulaPriceSubmitCheck =
  | { kind: 'ok' }
  /** 計算できた応答の手数料自動考慮の確認が要る (確認中・取得できない・設定の食い違い)。 */
  | { kind: 'feeBlocked'; reason: FeeAutoConsiderBlockedReason }
  /** 計算結果から求めた入札価格が値域・入札制限の外 (AC-BIN-104 の文言の元)。 */
  | { kind: 'outOfRange'; violation: FeeAdjustmentViolation }

/**
 * プレビューから、 1 コマのフォーミュラの札を作成・保存してよいかを決める (AC-BIN-004b / AC-BIN-104)。
 * 札作成・入札編集・一括作成のコマごとで共通。 計算できた応答の手数料の判定だけで止め、 取得不可・
 * 計算できない・取得失敗・問い合わせ中は止めない (保存時に backend が判定して拒否する。 AC-BIN-020)。
 */
export const formulaPriceSubmitCheck = ({
  price,
  fee,
}: FormulaPricePreviewResult): FormulaPriceSubmitCheck => {
  if (price.kind !== 'calculated') return { kind: 'ok' }
  switch (fee.kind) {
    case 'blocked':
      return { kind: 'feeBlocked', reason: fee.reason }
    case 'outOfRange':
      return { kind: 'outOfRange', violation: fee.violation }
    case 'idle':
    case 'ready':
    case 'fallbackOff':
      return { kind: 'ok' }
  }
}
